// Revise run — the question screen, all six modes.
//
// Options are shown in the dealt order (api.orders) and every choice is stored
// as the AUTHORED index. Shared feedback resolves `{optN}` to the dealt letter;
// per-option feedback shows the line for what the learner chose, and on a
// wrong answer the line for the answer as well.

import type { QuestionScreen as Q, ReviseRun } from '../../../../../lib/revise/types';
import { OPTION_LETTERS, gradeQuestion, resolveOptionRefs, marksOf } from '../../../../../lib/revise/scoring';
import type { ReviseRunApi } from './useReviseRun';
import { Chip, html } from './ContentScreens';

const marksLabel = (q: Q) => (q.marks === 'practice' ? 'Practice' : typeof q.marks === 'number' ? `${q.marks} mark${q.marks === 1 ? '' : 's'}` : q.assessed ? '' : 'Not scored');

const correctSet = (q: Q): number[] => (Array.isArray(q.correct) ? q.correct : q.correct === undefined ? [] : [q.correct]);

export function Question({ q, run, api, readOnly }: { q: Q; run: ReviseRun; api: ReviseRunApi; readOnly?: boolean }) {
  const order = api.orders[q.id] ?? (q.options ?? []).map((_, i) => i);
  const submitted = api.answers[q.id];
  const response = submitted ?? api.draft[q.id] ?? {};
  const locked = !!submitted || !!readOnly;
  const grade = submitted ? gradeQuestion(q, submitted) : null;
  const correct = correctSet(q);
  const twoByTwo = run.profile === 'paper' && q.mode === 'mcq' && marksOf(q) === 4 && !q.acceptAny;

  const setDraft = (r: Parameters<ReviseRunApi['setDraftFor']>[1]) => api.setDraftFor(q.id, r);

  const canSubmit = (() => {
    switch (q.mode) {
      case 'mcq':
      case 'sentence-mark':
      case 'build':
        return (response.selected?.length ?? 0) === 1;
      case 'multi':
        return (response.selected?.length ?? 0) === (q.maxSelect ?? correct.length);
      case 'order':
        return (response.sequence?.length ?? 0) === (q.items?.length ?? 0);
      case 'sort':
        return Object.keys(response.placement ?? {}).length === (q.items?.length ?? 0);
    }
  })();

  return (
    <>
      {run.profile === 'paper' ? (
        <ExamStem q={q} twoByTwo={twoByTwo} />
      ) : (
        <>
          <div className="rr-q-head">
            {q.kind === 'instinct' && <Chip text="Your instinct" kind="instinct" />}
            {q.tag && q.assessed && <Chip text={q.tag} kind="accent" />}
            {q.number && <Chip text={q.number} />}
            <span className="rr-q-marks">{marksLabel(q)}</span>
          </div>
          {q.script && <div className="rr-script">{q.script}</div>}
          <div className="rr-stem" {...html(q.stem)} />
        </>
      )}
      {twoByTwo && <div className="rr-note">In the exam this is worth 4 marks, so you need two explained points. Choose the best one here, then read the feedback for the second.</div>}
      {q.mode === 'order' && <OrderMode q={q} response={response} locked={locked} grade={grade} setDraft={setDraft} />}
      {q.mode === 'sort' && <SortMode q={q} response={response} locked={locked} grade={grade} setDraft={setDraft} />}
      {(q.mode === 'mcq' || q.mode === 'multi' || q.mode === 'sentence-mark' || q.mode === 'build') && (
        <OptionsMode q={q} order={order} response={response} locked={locked} grade={grade} setDraft={setDraft} />
      )}
      {!locked && (
        <div>
          <button type="button" className="rr-btn-primary" disabled={!canSubmit} onClick={() => api.submit(q)}>
            {q.mode === 'build' ? 'Add to summary' : 'Submit'}
          </button>
        </div>
      )}
      {(submitted || readOnly) && <Feedback q={q} order={order} response={response} grade={grade} />}
    </>
  );
}

/**
 * Practice Lab: the question is set the way the exam paper sets it, its number
 * in the margin and its marks bracketed on the right, at reading weight. The
 * display face stays in the band, never on the question.
 */
function ExamStem({ q, twoByTwo }: { q: Q; twoByTwo: boolean }) {
  const marks = typeof q.marks === 'number' ? `${twoByTwo ? '(2 x 2) ' : ''}(${q.marks})` : null;
  const meta = [q.skill ? q.skill[0].toUpperCase() + q.skill.slice(1) : null, q.tag].filter(Boolean).join(' · ');
  return (
    <>
      {meta && <div className="rr-exam-meta">{meta}</div>}
      <div className="rr-exam-stem">
        {q.number && <span className="rr-exam-n">{q.number}</span>}
        <div>
          <div {...html(q.stem)} />
          {marks && <span className="rr-exam-marks">{marks}</span>}
        </div>
      </div>
      {q.script && <div className="rr-script">{q.script}</div>}
    </>
  );
}

type Draft = (r: { selected?: number[]; sequence?: number[]; placement?: Record<string, number> }) => void;
type Resp = { selected?: number[]; sequence?: number[]; placement?: Record<string, number> };
type G = ReturnType<typeof gradeQuestion> | null;

function OptionsMode({ q, order, response, locked, grade, setDraft }: { q: Q; order: number[]; response: Resp; locked: boolean; grade: G; setDraft: Draft }) {
  const selected = response.selected ?? [];
  const cap = q.mode === 'multi' ? q.maxSelect ?? correctSet(q).length : 1;
  const atCap = q.mode === 'multi' && selected.length >= cap;
  const correct = correctSet(q);
  const toggle = (i: number) => {
    if (locked) return;
    if (q.mode === 'multi') {
      if (selected.includes(i)) setDraft({ selected: selected.filter((x) => x !== i) });
      else if (!atCap) setDraft({ selected: [...selected, i] });
    } else setDraft({ selected: [i] });
  };
  return (
    <>
      <div className="rr-opts" role={q.mode === 'multi' ? 'group' : 'radiogroup'}>
        {order.map((authored, pos) => {
          const text = q.options?.[authored] ?? '';
          const isSel = selected.includes(authored);
          let cls = 'rr-opt';
          if (grade) {
            if (correct.includes(authored) && (q.mode !== 'mcq' || !q.acceptAny || isSel || correct.length === 1)) cls += ' rr-ok';
            else if (isSel) cls += ' rr-no';
            else cls += ' rr-dim';
          } else if (isSel) cls += ' rr-sel';
          else if (atCap) cls += ' rr-dim';
          return (
            <button
              key={authored}
              type="button"
              className={cls}
              disabled={locked}
              aria-pressed={isSel}
              onClick={() => toggle(authored)}
            >
              <span className="rr-letter" aria-hidden>
                {OPTION_LETTERS[pos]}
              </span>
              <span {...html(text)} />
            </button>
          );
        })}
      </div>
      {q.mode === 'multi' && !locked && (
        <div className="rr-sel-count">
          {selected.length} of {cap} selected.{atCap ? ' To change your choice, deselect one first.' : ''}
        </div>
      )}
    </>
  );
}

function OrderMode({ q, response, locked, grade, setDraft }: { q: Q; response: Resp; locked: boolean; grade: G; setDraft: Draft }) {
  const items = q.items ?? [];
  const seq = response.sequence ?? items.map((_, i) => i);
  const move = (pos: number, dir: -1 | 1) => {
    if (locked) return;
    const next = [...seq];
    const j = pos + dir;
    if (j < 0 || j >= next.length) return;
    [next[pos], next[j]] = [next[j], next[pos]];
    setDraft({ sequence: next });
  };
  return (
    <div className="rr-order">
      {seq.map((itemIdx, pos) => {
        const right = grade ? q.sequence?.[pos] === itemIdx : null;
        return (
          <div key={itemIdx} className={`rr-order-row${grade ? (right ? ' rr-ok' : ' rr-no') : ''}`}>
            <span>
              {pos + 1}. {items[itemIdx]}
            </span>
            {!locked && (
              <span className="rr-order-btns">
                <button type="button" aria-label={`Move up: ${items[itemIdx]}`} onClick={() => move(pos, -1)} disabled={pos === 0}>
                  ↑
                </button>
                <button type="button" aria-label={`Move down: ${items[itemIdx]}`} onClick={() => move(pos, 1)} disabled={pos === seq.length - 1}>
                  ↓
                </button>
              </span>
            )}
          </div>
        );
      })}
      {!locked && !response.sequence && (
        <button type="button" className="rr-btn-quiet" onClick={() => setDraft({ sequence: seq })}>
          Keep this order
        </button>
      )}
    </div>
  );
}

function SortMode({ q, response, locked, grade, setDraft }: { q: Q; response: Resp; locked: boolean; grade: G; setDraft: Draft }) {
  const items = q.items ?? [];
  const cols = q.cols ?? [];
  const placement = response.placement ?? {};
  return (
    <div className="rr-sort">
      {items.map((it, i) => {
        const chosen = placement[String(i)];
        const right = grade ? q.placement?.[String(i)] === chosen : null;
        return (
          <div key={i} className={`rr-sort-item${grade ? (right ? ' rr-ok' : ' rr-no') : ''}`}>
            <span>{it}</span>
            <div className="rr-sort-cols" role="group" aria-label={it}>
              {cols.map((c, ci) => (
                <button
                  key={ci}
                  type="button"
                  className={chosen === ci ? 'rr-sel' : ''}
                  aria-pressed={chosen === ci}
                  disabled={locked}
                  onClick={() => setDraft({ placement: { ...placement, [String(i)]: ci } })}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Feedback({ q, order, response, grade }: { q: Q; order: number[]; response: Resp; grade: G }) {
  if (q.mode === 'build') {
    return <div className="rr-feedback rr-fb-neutral">{typeof q.feedback === 'string' ? <span {...html(resolveOptionRefs(q.feedback, order))} /> : null}</div>;
  }
  const ok = grade?.correct ?? false;
  const cls = q.assessed || q.kind !== 'instinct' ? (ok ? 'rr-fb-ok' : 'rr-fb-no') : 'rr-fb-neutral';
  const heading = q.kind === 'instinct' ? 'What that tells you' : ok ? 'Correct' : grade && grade.fraction > 0 && grade.fraction < 1 ? 'Partly right' : 'Not this one';
  if (Array.isArray(q.feedback)) {
    const chosen = response.selected?.[0];
    const correct = correctSet(q)[0];
    return (
      <div className={`rr-feedback ${cls}`}>
        <b>{heading}</b>
        {chosen !== undefined && <p {...html(q.feedback[chosen])} />}
        {!ok && correct !== undefined && chosen !== correct && (
          <p>
            <strong>{OPTION_LETTERS[order.indexOf(correct)]}.</strong> <span {...html(q.feedback[correct])} />
          </p>
        )}
      </div>
    );
  }
  return (
    <div className={`rr-feedback ${cls}`}>
      <b>{heading}</b>
      <span {...html(resolveOptionRefs(q.feedback, order))} />
    </div>
  );
}
